
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { PublicationStatusBadge } from '../PublicationStatusBadge';
import {
  resolveSessionUpdatedAtToken,
  withArtDisplayCacheBust,
} from '../utils/artDisplayCacheBust';

const ImagesScrollableContainer = ({
  images = [],
  totalImages = 0,
  onSelectImage,
  renderKey = 0,
  sessionDetail = null,
  imageWrapperClassName = '',
  scrollableImageClassName = '',
  hideImageDetails = false,
  editingPieceNumbers = [],
}) => {
  const { t } = useTranslation();
  const skeletonCount = Math.max(0, totalImages - images.length);
  const sessionUpdatedAt = resolveSessionUpdatedAtToken(sessionDetail);

  const getPieceNumber = (image) => {
    const pieceNumber = image?.pieceNumber ?? image?.piece_number;
    return typeof pieceNumber === 'number' ? pieceNumber : null;
  };

  const isPieceEditing = (pieceNumber) =>
    pieceNumber != null &&
    Array.isArray(editingPieceNumbers) &&
    editingPieceNumbers.includes(pieceNumber);

  // Build slots in piece_number order so skeletons occupy correct positions
  // when pieces arrive out of order from the backend.
  const slots = Array.from({ length: totalImages }, (_, index) => {
    const pieceNumber = index + 1;
    const art = images.find((img) => getPieceNumber(img) === pieceNumber);
    return { pieceNumber, art };
  });

  return (
    <div className={styles.container}>
      <div className={styles.imagesScrollableContainer}>
        {slots.map(({ pieceNumber, art }) => {
          if (art) {
            const imageIsEditing = isPieceEditing(pieceNumber);
            return (
              <div
                key={`${pieceNumber}-${art.revision ?? renderKey}`}
                className={`${styles.imageWrapper} ${imageWrapperClassName} ${imageIsEditing ? styles.imageWrapperEditing : ''}`}
                onClick={() => onSelectImage && onSelectImage(art)}
              >
                <div className={styles.imageFrame}>
                  <img
                    src={withArtDisplayCacheBust(
                      typeof art === 'string' ? art : art.url || art.imageUrl,
                      {
                        renderKey,
                        revision: art?.revision,
                        sessionUpdatedAt,
                      },
                    )}
                    alt={art.alt || t('chatsocket.images_scrollable.alt_fallback', { index: pieceNumber })}
                    className={`${styles.scrollableImage} ${scrollableImageClassName} ${imageIsEditing ? styles.scrollableImageEditing : ''}`}
                  />
                  {imageIsEditing && (
                    <div className={styles.editingOverlay}>
                      <div className={styles.editingSpinner} />
                      <span className={styles.editingText}>
                        {t('chatsocket.preview_art.editing')}
                      </span>
                    </div>
                  )}
                </div>
                {!hideImageDetails && (
                  <div className={styles.imageDetails}>
                    <p>{art.title || ''}</p>
                    {art?.publicationStatus && (
                      <PublicationStatusBadge status={art.publicationStatus} className={styles.statusBadge} />
                    )}
                  </div>
                )}
              </div>
            );
          }

          return (
            <div key={`skeleton-${pieceNumber}-${renderKey}`} className={styles.imageWrapperSkeleton}>
              <div className={styles.skeletonImage} />
              <div className={styles.skeletonText} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ImagesScrollableContainer;
